import { LinearProgress, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Toolbar, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import AppFunctionsService from '../../../services/appFunctionsService';
import GeneralFunctions from '../../../store/general/functions';
import '../../../style/transformer/budget/budgetTable.scss';
import BudgetEditDialog from './dialog/EditDialog';
import DataFunctions from '../../../store/data/functions';
import Screen from '../../customComponentV2/screen/Screen';
import TransformerBudgetHelper, { BudgetType, ITransformerBudget } from '../../../@types/model/transformer/budget/budget';

interface ITransformerBudgetListProps extends RouteComponentProps<never, never, {
    type: BudgetType;
}> {}

const TransformerBudgetList = (props: ITransformerBudgetListProps): React.ReactElement => {
    const [type, setType] = useState<BudgetType | undefined>(undefined);
    const [budgets, setBudgets] = useState<ITransformerBudget[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const rowsPerPageOptions = [10, 20, 30, 50, 100];

    useEffect(() => {
        DataFunctions.getCompanies();
        setTypeFromUrl();
    }, []);

    useEffect(() => {
        setTypeFromUrl();
    }, [props.location.search]);

    useEffect(() => {
        if (!type) return;

        setIsLoading(true);
        
        const unsubscribe = TransformerBudgetHelper.collection(type)
            .where('type', '==', type)
            .orderBy('startDate', 'asc')
            .onSnapshot((snapshot) => {

            const fetchedDocs : Array<ITransformerBudget> = [];
                
            snapshot.forEach((doc) => {
                fetchedDocs.push(doc.data());
            });

            setBudgets(fetchedDocs);
            setIsLoading(false);

        }, (err) => {
            GeneralFunctions.generalShowError(err.message, 'An error occurred while loading budgets.');
            setIsLoading(false);
        });
        
        return (() => unsubscribe());
    }, [type]);

    const setTypeFromUrl = (): void => {
        const urlParams = new URLSearchParams(props.location.search);
        const t = urlParams.get('type') as BudgetType;
        setType(t);
    };

    const onPageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number): void => {
        setCurrentPage(page);
    };

    const onRowsPerPageChange = (event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (!event) return;
        setRowsPerPage(Number(event.target.value));
    };

    const getFilterBudgets = useMemo<ITransformerBudget[]>(() => {
        return budgets.slice((currentPage * rowsPerPage), currentPage * rowsPerPage + rowsPerPage);
    }, [budgets, rowsPerPage, currentPage]);

    return (
        <Screen
            useAnimatedLoader
            isLoading={isLoading}>
            <div className={'fdc flx1 p10 mh0 mw0'}>
                <div className={'flx1 fdc mh0 mw0'}>
                    <Toolbar>
                        <Typography color='inherit' className='mr20 fs25 fwm cpd'>
                            {type?.toLocaleUpperCase() ?? ''} BUDGETED UNITS
                        </Typography>
                        {
                            type &&
                            <BudgetEditDialog type={type} budgets={budgets} />
                        }
                    </Toolbar>
                    <div className={'hfill fdc mh0 mw0'}>
                        <div className={'flx1 fdc oxs oys wfill hfill'}>
                            <Table stickyHeader>
                                <TableHead className='budget-table-header'>
                                    <TableRow>
                                        <TableCell className='bcp' component='th' scope='row' align='left'>
                                            Company
                                        </TableCell>
                                        <TableCell className='bcp' component='th' scope='row' align='left'>
                                            Area
                                        </TableCell>
                                        <TableCell className='bcp' component='th' scope='row' align='left'>
                                            Start Date
                                        </TableCell>
                                        <TableCell component='th' scope='row' align='left'>
                                            End Date
                                        </TableCell>
                                        <TableCell component='th' scope='row' align='left'>
                                            Created By
                                        </TableCell>
                                        <TableCell component='th' scope='row' align='left'>
                                            Created On
                                        </TableCell>
                                        <TableCell component='th' scope='row' align='left'>
                                            Update By
                                        </TableCell>
                                        <TableCell component='th' scope='row' align='left'>
                                            Updated On
                                        </TableCell>
                                        <TableCell component='th' scope='row' align='left'>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody className='budget-table-body'>
                                    {
                                        getFilterBudgets.map(row => (
                                            <TableRow key={row.id} className={'data-row'}>
                                                <TableCell>
                                                    {
                                                        row.companyName
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    {
                                                        row.area
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    {
                                                        AppFunctionsService.formatDateTimeToDateOnly(row.startDate)
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    {
                                                        AppFunctionsService.formatDateTimeToDateOnly(row.endDate)
                                                    }
                                                </TableCell>
                                                <TableCell component='td' scope='row' align='left'>
                                                    <Tooltip title={row.createdByEmployee}>
                                                        <div>
                                                            {
                                                                row.createdByName
                                                            }
                                                        </div>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell component='td' scope='row' align='left'>
                                                    {
                                                        AppFunctionsService.formatDateTime(row.createdOn)
                                                    }
                                                </TableCell>
                                                <TableCell component='td' scope='row' align='left'>
                                                    <Tooltip title={row.updatedByEmployee}>
                                                        <div>
                                                            {
                                                                row.updatedByName
                                                            }
                                                        </div>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell component='td' scope='row' align='left'>
                                                    {
                                                        AppFunctionsService.formatDateTime(row.updatedOn)
                                                    }
                                                </TableCell>
                                                <TableCell component='td' scope='row' align='left'>
                                                    <BudgetEditDialog type={row.type} value={row} budgets={budgets} />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </div>
                        <div className={'fdr aic'}>
                            <TablePagination
                                component='div'
                                rowsPerPageOptions={rowsPerPageOptions}
                                count={budgets.length}
                                rowsPerPage={rowsPerPage}
                                page={currentPage}
                                onPageChange={onPageChange}
                                onRowsPerPageChange={onRowsPerPageChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Screen>
    );
};

export default TransformerBudgetList;
